import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserCircle, LockKey, Envelope, PencilSimple } from "@phosphor-icons/react";

export default function ProfileCard() {
  const { user, refresh } = useAuth();
  const isCompany = user?.role === "company";
  const isAutonomo = user?.role === "autonomo";
  const isDriverEmpregado = user?.role === "driver" || (user?.role === "autonomo" && user?.driver_mode === "empregado");

  const [editData, setEditData] = useState(false);
  const [dataForm, setDataForm] = useState({
    name: user?.name || "",
    company_name: user?.company_name || "",
    phone: user?.phone || "",
    cnpj: user?.cnpj || "",
    cpf: user?.cpf || "",
    employer_name: user?.employer_name || "",
    employer_phone: user?.employer_phone || "",
    contractor_name: user?.contractor_name || "",
    contractor_cnpj: user?.contractor_cnpj || "",
  });
  const [savingData, setSavingData] = useState(false);

  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ current_password: "", new_email: "" });
  const [savingEmail, setSavingEmail] = useState(false);

  const saveData = async () => {
    setSavingData(true);
    try {
      const payload = { phone: dataForm.phone };
      if (isCompany) { payload.company_name = dataForm.company_name; payload.cnpj = dataForm.cnpj; }
      else { payload.name = dataForm.name; payload.cpf = dataForm.cpf; }
      if (isAutonomo) { payload.company_name = dataForm.company_name; payload.cnpj = dataForm.cnpj; payload.contractor_name = dataForm.contractor_name; payload.contractor_cnpj = dataForm.contractor_cnpj; }
      if (isDriverEmpregado) { payload.employer_name = dataForm.employer_name; payload.employer_phone = dataForm.employer_phone; }
      await api.put("/auth/profile", payload);
      toast.success("Dados atualizados!");
      setEditData(false);
      if (refresh) await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSavingData(false); }
  };

  const savePassword = async () => {
    if (pwForm.new_password !== pwForm.confirm) { toast.error("As senhas não conferem"); return; }
    if (pwForm.new_password.length < 6) { toast.error("Nova senha precisa de ao menos 6 caracteres"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success("Senha alterada com sucesso!");
      setPwOpen(false);
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao trocar senha");
    } finally { setSavingPw(false); }
  };

  const saveEmail = async () => {
    if (!emailForm.new_email.includes("@")) { toast.error("E-mail inválido"); return; }
    setSavingEmail(true);
    try {
      await api.post("/auth/change-email", emailForm);
      toast.success("E-mail alterado! Use o novo e-mail no próximo login.");
      setEmailOpen(false);
      setEmailForm({ current_password: "", new_email: "" });
      if (refresh) await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao trocar e-mail");
    } finally { setSavingEmail(false); }
  };

  return (
    <>
      <Card data-testid="profile-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-lg flex items-center gap-2"><UserCircle size={22} weight="fill" className="text-blue-600" /> Meus dados</CardTitle>
          {!editData && (
            <Button size="sm" variant="outline" onClick={() => setEditData(true)} data-testid="btn-edit-profile" className="gap-1">
              <PencilSimple size={14} /> Editar
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {!editData ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              {isCompany ? (
                <>
                  <div><span className="text-slate-500">Razão social:</span> <strong>{user?.company_name || "-"}</strong></div>
                  <div><span className="text-slate-500">CNPJ:</span> <strong>{user?.cnpj || "-"}</strong></div>
                </>
              ) : (
                <>
                  <div><span className="text-slate-500">Nome:</span> <strong>{user?.name || "-"}</strong></div>
                  <div><span className="text-slate-500">CPF:</span> <strong>{user?.cpf || "-"}</strong></div>
                </>
              )}
              {isAutonomo && (
                <>
                  <div><span className="text-slate-500">Nome da empresa:</span> <strong data-testid="profile-view-company-name">{user?.company_name || "-"}</strong></div>
                  <div><span className="text-slate-500">CNPJ ou CPF:</span> <strong data-testid="profile-view-cnpj">{user?.cnpj || "-"}</strong></div>
                  <div><span className="text-slate-500">Presto serviço para:</span> <strong data-testid="profile-view-contractor">{user?.contractor_name || "-"}</strong></div>
                  <div><span className="text-slate-500">CNPJ da contratante:</span> <strong data-testid="profile-view-contractor-cnpj">{user?.contractor_cnpj || "-"}</strong></div>
                </>
              )}
              <div><span className="text-slate-500">E-mail:</span> <strong>{user?.email}</strong></div>
              <div><span className="text-slate-500">Telefone:</span> <strong>{user?.phone || "-"}</strong></div>
              {isDriverEmpregado && (
                <>
                  <div><span className="text-slate-500">Patrão:</span> <strong>{user?.employer_name || "-"}</strong></div>
                  <div><span className="text-slate-500">Contato patrão:</span> <strong>{user?.employer_phone || "-"}</strong></div>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {isCompany ? (
                <>
                  <div><Label>Razão social</Label><Input value={dataForm.company_name} onChange={(e) => setDataForm({ ...dataForm, company_name: e.target.value })} data-testid="profile-company-name" /></div>
                  <div><Label>CNPJ</Label><Input value={dataForm.cnpj} onChange={(e) => setDataForm({ ...dataForm, cnpj: e.target.value })} placeholder="00.000.000/0000-00" data-testid="profile-cnpj" /></div>
                </>
              ) : (
                <>
                  <div><Label>Nome completo</Label><Input value={dataForm.name} onChange={(e) => setDataForm({ ...dataForm, name: e.target.value })} data-testid="profile-name" /></div>
                  <div><Label>CPF</Label><Input value={dataForm.cpf} onChange={(e) => setDataForm({ ...dataForm, cpf: e.target.value })} placeholder="000.000.000-00" data-testid="profile-cpf" /></div>
                </>
              )}
              {isAutonomo && (
                <>
                  <div><Label>Nome da empresa / Razão social</Label><Input value={dataForm.company_name} onChange={(e) => setDataForm({ ...dataForm, company_name: e.target.value })} placeholder="Sai nos relatórios e no acerto" data-testid="profile-company-name" /></div>
                  <div><Label>CNPJ ou CPF</Label><Input value={dataForm.cnpj} onChange={(e) => setDataForm({ ...dataForm, cnpj: e.target.value })} placeholder="00.000.000/0000-00 ou 000.000.000-00" data-testid="profile-cnpj" /></div>
                  <div><Label>Empresa para quem presto serviço</Label><Input value={dataForm.contractor_name} onChange={(e) => setDataForm({ ...dataForm, contractor_name: e.target.value })} placeholder="Ex: Transportadora XYZ Ltda" data-testid="profile-contractor-name" /></div>
                  <div><Label>CNPJ da contratante</Label><Input value={dataForm.contractor_cnpj} onChange={(e) => setDataForm({ ...dataForm, contractor_cnpj: e.target.value })} placeholder="00.000.000/0000-00" data-testid="profile-contractor-cnpj" /></div>
                </>
              )}
              <div><Label>Telefone / WhatsApp</Label><Input value={dataForm.phone} onChange={(e) => setDataForm({ ...dataForm, phone: e.target.value })} placeholder="(65) 9 9999-9999" data-testid="profile-phone" /></div>
              {isDriverEmpregado && (
                <>
                  <div><Label>Nome do patrão</Label><Input value={dataForm.employer_name} onChange={(e) => setDataForm({ ...dataForm, employer_name: e.target.value })} data-testid="profile-employer-name" /></div>
                  <div><Label>Contato do patrão</Label><Input value={dataForm.employer_phone} onChange={(e) => setDataForm({ ...dataForm, employer_phone: e.target.value })} data-testid="profile-employer-phone" /></div>
                </>
              )}
              <div className="md:col-span-2 flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setEditData(false)} data-testid="btn-cancel-profile">Cancelar</Button>
                <Button size="sm" onClick={saveData} disabled={savingData} data-testid="btn-save-profile" className="bg-blue-600 hover:bg-blue-700 text-white">
                  {savingData ? "Salvando..." : "Salvar alterações"}
                </Button>
              </div>
            </div>
          )}

          <div className="border-t pt-3 flex flex-col sm:flex-row gap-2">
            <Button variant="outline" size="sm" onClick={() => setPwOpen(true)} data-testid="btn-change-password" className="gap-1">
              <LockKey size={14} /> Trocar senha
            </Button>
            <Button variant="outline" size="sm" onClick={() => setEmailOpen(true)} data-testid="btn-change-email" className="gap-1">
              <Envelope size={14} /> Trocar e-mail
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Change password dialog */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><LockKey size={20} /> Trocar senha</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Senha atual</Label><Input type="password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} data-testid="pw-current" autoFocus /></div>
            <div><Label>Nova senha</Label><Input type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} placeholder="Mínimo 6 caracteres" data-testid="pw-new" /></div>
            <div><Label>Confirme a nova senha</Label><Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="pw-confirm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)}>Cancelar</Button>
            <Button onClick={savePassword} disabled={savingPw} data-testid="btn-save-password" className="bg-blue-600 hover:bg-blue-700 text-white">
              {savingPw ? "Salvando..." : "Trocar senha"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change email dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Envelope size={20} /> Trocar e-mail</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
              ⚠️ Você usará o novo e-mail para fazer login. Anote antes de confirmar.
            </div>
            <div><Label>E-mail atual</Label><Input value={user?.email || ""} disabled className="bg-slate-50" /></div>
            <div><Label>Novo e-mail</Label><Input type="email" value={emailForm.new_email} onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })} placeholder="novo@email.com" data-testid="email-new" autoFocus /></div>
            <div><Label>Sua senha (confirmação)</Label><Input type="password" value={emailForm.current_password} onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })} data-testid="email-password" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancelar</Button>
            <Button onClick={saveEmail} disabled={savingEmail} data-testid="btn-save-email" className="bg-blue-600 hover:bg-blue-700 text-white">
              {savingEmail ? "Salvando..." : "Trocar e-mail"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
